import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Mail, Phone, MapPin, Building2, Lock, Save, KeyRound } from "lucide-react";
import { apiBoss } from "@/lib/api";
import { VerifiedMobileInput, VerifiedEmailInput } from "@/components/common/VerifiedInput";

export default function BossSettingsPage() {
  const [settings, setSettings] = useState({
    support_phone: "9876543210",
    support_email: "support@tablezy.com",
    location: "Tech Park, Jaipur & Delhi, India",
    company_name: "Tablezy Technologies",
  });
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  // Password reset form state
  const [pwForm, setPwForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [resettingPw, setResettingPw] = useState(false);

  useEffect(() => {
    apiBoss.get("/boss/settings")
      .then(({ data }) => {
        if (data.settings) setSettings(data.settings);
      })
      .catch(() => toast.error("Failed to load settings"))
      .finally(() => setLoadingSettings(false));
  }, []);

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    if (!settings.support_phone.trim() || !settings.support_email.trim() || !settings.location.trim()) {
      return toast.error("Phone, Email, and Location are required.");
    }

    setSavingSettings(true);
    try {
      await apiBoss.put("/boss/settings", settings);
      toast.success("Global contact & platform settings saved! Customer panel updated dynamically.");
    } catch (err) {
      toast.error("Failed to update settings.");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!pwForm.current_password || !pwForm.new_password) {
      return toast.error("Please enter current and new passwords.");
    }
    if (pwForm.new_password !== pwForm.confirm_password) {
      return toast.error("New passwords do not match.");
    }
    if (pwForm.new_password.length < 4) {
      return toast.error("Password must be at least 4 characters long.");
    }

    setResettingPw(true);
    try {
      await apiBoss.post("/boss/change-password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Boss Password reset successfully!");
      setPwForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reset password. Incorrect current password.");
    } finally {
      setResettingPw(false);
    }
  };

  return (
    <div className="space-y-10 max-w-4xl mx-auto">
      {/* Page Header */}
      <div className="border-b border-slate-800 pb-6">
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary font-semibold">System Configuration</span>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mt-1">Platform Settings & Security</h1>
        <p className="text-slate-400 text-xs mt-1">
          Changes to Support Phone, Email, and Location automatically sync across the entire Customer Panel in real time.
        </p>
      </div>

      {/* 1. Global Contact & Platform Settings Form */}
      <form onSubmit={handleSaveSettings} className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <div className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-xl font-bold text-white">Global Contact & Customer Support Settings</h3>
          </div>
          <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20">
            Real-Time Sync Active
          </span>
        </div>

        {loadingSettings ? (
          <div className="py-8 text-center text-slate-500 font-mono text-xs">Loading Settings...</div>
        ) : (
          <div className="space-y-4 text-xs">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Support Phone Number */}
              <VerifiedMobileInput
                label="Official Support Phone Number * (10-Digit Count)"
                value={settings.support_phone}
                onChange={(val) => setSettings({ ...settings, support_phone: val })}
              />

              {/* Support Email */}
              <VerifiedEmailInput
                label="Official Support Email Address *"
                value={settings.support_email}
                onChange={(val) => setSettings({ ...settings, support_email: val })}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Operating Location */}
              <div>
                <label className="block font-mono uppercase text-slate-400 mb-1 font-semibold">
                  Operating Headquarters Location *
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    required
                    placeholder="Tech Park, Jaipur & Delhi, India"
                    value={settings.location}
                    onChange={(e) => setSettings({ ...settings, location: e.target.value })}
                    className="w-full h-11 pl-10 pr-3 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm"
                  />
                </div>
              </div>

              {/* Company / Entity Name */}
              <div>
                <label className="block font-mono uppercase text-slate-400 mb-1 font-semibold">
                  Company / Entity Legal Name
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    value={settings.company_name}
                    onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                    className="w-full h-11 pl-10 pr-3 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="pt-3 flex justify-end">
              <button
                type="submit"
                disabled={savingSettings}
                className="h-11 px-6 rounded-full bg-primary text-white text-xs font-semibold flex items-center gap-2 hover:bg-primary/90 transition-all disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>{savingSettings ? "Saving Settings..." : "Save Global Settings"}</span>
              </button>
            </div>
          </div>
        )}
      </form>

      {/* 2. Reset Boss Password Card */}
      <form onSubmit={handleChangePassword} className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <div className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-amber-400" />
            <h3 className="font-heading text-xl font-bold text-white">Reset Boss Password</h3>
          </div>
          <span className="text-[10px] font-mono text-slate-400">Encrypted Bcrypt Hashing</span>
        </div>

        <div className="space-y-4 text-xs">
          <div>
            <label className="block font-mono uppercase text-slate-400 mb-1 font-semibold">
              Current Boss Password *
            </label>
            <input
              type="password"
              required
              placeholder="••••••••"
              value={pwForm.current_password}
              onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
              className="w-full h-11 px-3 rounded-xl bg-slate-950 border border-slate-800 text-white font-mono text-sm"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-mono uppercase text-slate-400 mb-1 font-semibold">
                New Password *
              </label>
              <input
                type="password"
                required
                placeholder="••••••••"
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                className="w-full h-11 px-3 rounded-xl bg-slate-950 border border-slate-800 text-white font-mono text-sm"
              />
            </div>

            <div>
              <label className="block font-mono uppercase text-slate-400 mb-1 font-semibold">
                Confirm New Password *
              </label>
              <input
                type="password"
                required
                placeholder="••••••••"
                value={pwForm.confirm_password}
                onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })}
                className="w-full h-11 px-3 rounded-xl bg-slate-950 border border-slate-800 text-white font-mono text-sm"
              />
            </div>
          </div>

          <div className="pt-2 flex justify-end">
            <button
              type="submit"
              disabled={resettingPw}
              className="h-11 px-6 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold text-xs flex items-center gap-2 transition-all disabled:opacity-50"
            >
              <Lock className="w-4 h-4" />
              <span>{resettingPw ? "Resetting Password..." : "Update Boss Password"}</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
